import { useCallback, useEffect, useRef } from 'react'

// ============================================================
// useVisibilityTrigger — 純 observer，不再管 active state
// ------------------------------------------------------------
// 職責單一：任何被 register 過的 element 可見度 ≥ ACTIVATION_RATIO
// 時呼叫 onVisible(key)。state 由呼叫端持有（例如 Home 的 activeMomentId）。
//
// 為什麼這樣切？
//   - 以前 useScrollPlayback 同時管 observer + activeIndex + 重置邏輯，
//     切分類時 remount 一來就有三重 race；且 state 跟 DOM 結構綁死，
//     難獨立測試。
//   - 現在 hook 不知道 active 是什麼，只告訴呼叫端「這個 key 夠可見了」。
//     decoupled，搭配單一 ActivePlayer 架構乾淨。
//
// observer 只建一次，element attach/detach 直接 observe/unobserve → 對
// AnimatePresence 的 remount 免疫。
// ============================================================

const ACTIVATION_RATIO = 0.7
const DEBOUNCE_MS = 80

export function useVisibilityTrigger(
  onVisible: (key: string) => void
): (key: string) => (el: HTMLElement | null) => void {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const itemsRef = useRef<Map<string, HTMLElement>>(new Map())
  const onVisibleRef = useRef(onVisible)
  const lastFiredRef = useRef<string | null>(null)
  const timerRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    onVisibleRef.current = onVisible
  }, [onVisible])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.intersectionRatio >= ACTIVATION_RATIO) {
            const key = (e.target as HTMLElement).dataset.visKey
            if (!key || lastFiredRef.current === key) continue
            lastFiredRef.current = key
            window.clearTimeout(timerRef.current)
            timerRef.current = window.setTimeout(() => {
              onVisibleRef.current(key)
            }, DEBOUNCE_MS)
          }
        }
      },
      { threshold: [ACTIVATION_RATIO] }
    )
    observerRef.current = observer
    return () => {
      observer.disconnect()
      observerRef.current = null
      window.clearTimeout(timerRef.current)
    }
  }, [])

  return useCallback(
    (key: string) => (el: HTMLElement | null) => {
      const stored = itemsRef.current.get(key)
      if (stored && stored !== el) observerRef.current?.unobserve(stored)
      if (el) {
        el.dataset.visKey = key
        itemsRef.current.set(key, el)
        observerRef.current?.observe(el)
      } else if (stored) {
        itemsRef.current.delete(key)
      }
    },
    []
  )
}
